import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Joiner / mover / leaver automation',
    metaDescription:
      'Pre-day-one provisioning, atomic role-move access changes, and same-day offboarding, driven by your HRIS, propagated through every app.',
    heroEyebrow: 'Use cases · Joiner / Mover / Leaver',
    heroTitle: ['Provision in minutes.', 'Deprovision in seconds.'],
    heroBody:
      'From the HRIS event to every connected app: joiner, mover, and leaver flows that actually finish. No spreadsheets, no orphaned accounts, no 3am tickets.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'HRIS-driven automation',
      'Atomic role-move changes',
      'Same-day offboarding',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'The four lifecycle events, all automated.',
    pillarsBody:
      'Lifecycle is where most identity programs leak risk. Monofor closes the four common gaps (joiner, mover, leaver, and contractor expiry) on one platform.',
    pillars: [
      {
        title: 'Joiner: access ready before day one',
        description:
          'When a new hire is offered the role, Monofor pre-provisions the bundle so their email, SSO, MFA, and apps are ready when they walk in.',
        bullets: [
          'Role-based access bundles',
          'Pre-day-one provisioning from HRIS events',
          'Welcome flow with self-service MFA enrollment',
          'Manager approval for non-standard access',
        ],
      },
      {
        title: 'Mover: the worst kind of access risk',
        description:
          'Role changes accumulate entitlements unless someone explicitly takes them away. Monofor revokes old access in the same workflow that grants new.',
        bullets: [
          'Diff-driven access changes on role move',
          'Revoke + grant in one atomic workflow',
          'Manager approval on retained entitlements',
          'Audit trail of every retained vs revoked grant',
        ],
      },
      {
        title: 'Leaver: every account, every app, in seconds',
        description:
          'On the termination event, Monofor disables identity in the directory, revokes app access through connectors, and rotates any shared credentials.',
        bullets: [
          'Immediate identity disable on HRIS event',
          'Connector-driven app deprovisioning',
          'Shared-credential rotation',
          'Same-day evidence packet for HR / legal',
        ],
      },
      {
        title: 'Contractor and partner expiry',
        description:
          'Time-bound identity for contractors, vendors, and partners. Hard expiry dates that fire even if nobody remembers to follow up.',
        bullets: [
          'Hard-expiry on contractor accounts',
          'Renewal workflow with sponsor approval',
          'Vendor-org-level deprovisioning',
          'Per-org isolation in audit trail',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Wire. Model. Automate.',
    steps: [
      {
        title: 'Wire HRIS as the trigger',
        description:
          'Connect Workday, BambooHR, or your HRIS as the lifecycle event source. Monosign listens and propagates.',
      },
      {
        title: 'Model access by role',
        description:
          'Define role bundles that map to job functions. Birthright vs requestable access becomes an explicit decision.',
      },
      {
        title: 'Automate every event',
        description:
          'Joiner, mover, leaver, contractor expiry: every event triggers the right access change on the right system, every time.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'User lifecycle',
        description: 'The Monosign capability that powers JML automation.',
        href: '/sign/lifecycle',
      },
      {
        label: 'Monosync Connectors',
        description: 'The propagation mesh that pushes JML changes downstream.',
        href: '/sync/connectors',
      },
      {
        label: 'Identity governance',
        description: 'Entitlement reviews on top of the lifecycle layer.',
        href: '/sync/governance',
      },
    ],
  },
  tr: {
    metaTitle: 'İşe Giriş / Geçiş / Ayrılış otomasyonu',
    metaDescription:
      'İlk günden önce erişim sağlama, rol geçişinde atomik erişim değişiklikleri ve aynı gün işten çıkış: HRIS’inizden tetiklenir, tüm uygulamalara yayılır.',
    heroEyebrow: 'Kullanım senaryoları · İşe Giriş / Geçiş / Ayrılış',
    heroTitle: ['Dakikalar içinde erişim verin.', 'Saniyeler içinde kaldırın.'],
    heroBody:
      'HRIS olayından bağlı her uygulamaya: gerçekten tamamlanan işe giriş, geçiş ve ayrılış akışları. Elektronik tablo yok, sahipsiz hesap yok, gece 3 destek bileti yok.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'HRIS güdümlü otomasyon',
      'Rol geçişinde atomik değişiklikler',
      'Aynı gün işten çıkış',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Dört yaşam döngüsü olayı, tamamı otomatik.',
    pillarsBody:
      'Yaşam döngüsü, çoğu kimlik programının risk sızdırdığı yerdir. Monofor dört yaygın açığı (işe giriş, geçiş, ayrılış ve yüklenici süre dolumu) tek platformda kapatır.',
    pillars: [
      {
        title: 'İşe giriş: erişim ilk günden önce hazır',
        description:
          'Yeni çalışana rol teklif edildiğinde Monofor erişim paketini önceden hazırlar; e-posta, SSO, MFA ve uygulamalar kapıdan girdiğinde hazırdır.',
        bullets: [
          'Rol tabanlı erişim paketleri',
          'HRIS olaylarından ilk gün öncesi erişim sağlama',
          'Self servis MFA kaydı içeren karşılama akışı',
          'Standart dışı erişim için yönetici onayı',
        ],
      },
      {
        title: 'Geçiş: erişim riskinin en kötü türü',
        description:
          'Rol değişiklikleri, biri açıkça geri almadıkça yetki biriktirir. Monofor eski erişimi, yenisini veren aynı iş akışında geri alır.',
        bullets: [
          'Rol geçişinde fark temelli erişim değişiklikleri',
          'Tek atomik iş akışında geri alma + verme',
          'Korunan yetkilerde yönetici onayı',
          'Korunan ve geri alınan her yetkinin denetim izi',
        ],
      },
      {
        title: 'Ayrılış: her hesap, her uygulama, saniyeler içinde',
        description:
          'İşten ayrılma olayında Monofor dizindeki kimliği devre dışı bırakır, bağlayıcılar üzerinden uygulama erişimini geri alır ve paylaşılan kimlik bilgilerini döndürür.',
        bullets: [
          'HRIS olayında anında kimlik devre dışı bırakma',
          'Bağlayıcı güdümlü uygulama erişimi kaldırma',
          'Paylaşılan kimlik bilgisi rotasyonu',
          'İK / hukuk için aynı gün kanıt paketi',
        ],
      },
      {
        title: 'Yüklenici ve iş ortağı süre dolumu',
        description:
          'Yükleniciler, tedarikçiler ve iş ortakları için süreli kimlik. Kimse takip etmeyi hatırlamasa bile devreye giren kesin son kullanma tarihleri.',
        bullets: [
          'Yüklenici hesaplarında kesin süre dolumu',
          'Sponsor onaylı yenileme iş akışı',
          'Tedarikçi kuruluşu düzeyinde erişim kaldırma',
          'Denetim izinde kuruluş bazında izolasyon',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Bağlayın. Modelleyin. Otomatikleştirin.',
    steps: [
      {
        title: 'HRIS’i tetikleyici yapın',
        description:
          'Workday, BambooHR veya HRIS’inizi yaşam döngüsü olay kaynağı olarak bağlayın. Monosign dinler ve yayar.',
      },
      {
        title: 'Erişimi role göre modelleyin',
        description:
          'İş fonksiyonlarına karşılık gelen rol paketleri tanımlayın. Doğuştan gelen ve talep edilebilir erişim açık bir karara dönüşür.',
      },
      {
        title: 'Her olayı otomatikleştirin',
        description:
          'İşe giriş, geçiş, ayrılış, yüklenici süre dolumu: her olay, doğru sistemde doğru erişim değişikliğini her seferinde tetikler.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Kullanıcı yaşam döngüsü',
        description: 'JML otomasyonunu çalıştıran Monosign yeteneği.',
        href: '/sign/lifecycle',
      },
      {
        label: 'Monosync bağlayıcıları',
        description: 'JML değişikliklerini aşağı akışa ileten yayılım ağı.',
        href: '/sync/connectors',
      },
      {
        label: 'Kimlik yönetişimi',
        description: 'Yaşam döngüsü katmanının üzerinde yetki gözden geçirmeleri.',
        href: '/sync/governance',
      },
    ],
  },
}

type Props = { locale: string }

export default function JoinerMoverLeaverPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/joiner-mover-leaver"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
